/**
 * The office's work-order writes (fieldiq-api#905): open one on a tool, edit
 * one, cancel one. Completing reuses the field shell's dialog
 * (field/FieldWorkOrderScreen `CompleteWorkOrderDialog`), so a completion
 * sends the same body from the bench and from the office.
 *
 * OPEN. `POST /components/:id/inspections` takes the inspection types only
 * (inspection, recertification, magnaflux) and writes "Inspection of <serial>"
 * when no description is given; `POST /components/:id/work-orders` needs a
 * description. "Log inspection" uses the first, "Open work order" the second,
 * and the work-order list leaves the inspection types out so the two buttons
 * do not offer the same thing. Either is refused (409 COMPONENT_NOT_IN_SHOP)
 * unless the tool is in the shop — available, maintenance or failed — so the
 * screens offer them on exactly those statuses. An available tool moves to
 * maintenance.
 *
 * EDIT sends only what changed. The parts list is sent WHOLE (it replaces the
 * stored one; `null` clears it), each line `{ part, qty, cost }` with cost PER
 * UNIT. `parts_cost` is the API's sum of it and is shown as returned, never
 * added up here.
 *
 * CANCEL, like completing, can be the order that returns the tool to stock —
 * which the API does only behind dope & cleaning (409 DOPE_CLEAN_UNCONFIRMED).
 * The screen cannot see whether this is the tool's last open order, so the box
 * is offered and the 409 is shown, by its code, with the box to tick.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { useOpenWorkOrder, useUpdateWorkOrder } from '../../hooks/useDownhole';
import { unwrapArray } from '../../lib/api-envelope';
import { onlineOrSay } from '../../lib/downhole-offline';
import { DH_PRIORITY_LABEL, DH_WORK_TYPE_LABEL } from '../../lib/downhole-labels';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import {
  DH_INSPECTION_WORK_TYPES,
  DH_WORK_TYPES,
  DH_WO_PRIORITIES,
  type DhPartLine,
  type DhWorkOrder,
  type DhWorkOrderWrite,
} from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhField } from './DownholeBits';

const HOURS = /^\d+(\.\d+)?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER = /^\d+(\.\d+)?$/;

/** The statuses a work order can be opened on (api#905 openWorkOrder). */
export const DH_SHOP_STATUSES = ['available', 'maintenance', 'failed'] as const;
export const isInShop = (status: string | null | undefined) => !!status && (DH_SHOP_STATUSES as readonly string[]).includes(status);

/** By the API's machine code (api#913), never the sentence. */
export function conflictCode(error: unknown): string | null {
  const res = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  return res?.status === 409 ? res.data?.code ?? null : null;
}

export interface Assignee {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role?: string;
}
export const personName = (u: Assignee) => [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '—';

/** The tenant's people who can be given shop work: everyone but customers. */
export function useDownholeAssignees(enabled = true) {
  const query = useQuery({
    queryKey: ['users', 'downhole-assignees'],
    queryFn: async () => unwrapArray<Assignee>((await api.users.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  return { ...query, people: (query.data ?? []).filter((u) => u.role !== 'customer') };
}

const OPEN_WORK_TYPES = DH_WORK_TYPES.filter((t) => !(DH_INSPECTION_WORK_TYPES as readonly string[]).includes(t));

export function OpenWorkOrderDialog({
  open,
  onClose,
  kind,
  component,
  canAssign,
}: {
  open: boolean;
  onClose: () => void;
  kind: 'inspection' | 'work_order';
  component: { id: string; serial_number: string };
  /** Office roles pick an assignee here; otherwise the order opens unassigned. */
  canAssign: boolean;
}) {
  const openWo = useOpenWorkOrder();
  const assignees = useDownholeAssignees(open && canAssign);
  const inspection = kind === 'inspection';
  const types: readonly string[] = inspection ? DH_INSPECTION_WORK_TYPES : OPEN_WORK_TYPES;
  const [workType, setWorkType] = useState(inspection ? 'inspection' : '');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [assignedTo, setAssignedTo] = useState('');
  const [due, setDue] = useState('');

  const submit = async () => {
    const problems: string[] = [];
    if (!workType) problems.push('Choose the type of work');
    if (!inspection && !description.trim()) problems.push('Say what the work is');
    if (due && !DAY.test(due)) problems.push('Due must be a date');
    if (problems.length) {
      toast.error(problems.join('. '));
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    const body: DhWorkOrderWrite = { work_type: workType, priority };
    if (description.trim()) body.description = description.trim();
    if (assignedTo) body.assigned_to = assignedTo;
    if (due) body.due_date = due;
    try {
      const wo = await openWo.mutateAsync({ componentId: component.id, kind, body });
      toast.success(`${wo.data?.wo_number ?? (inspection ? 'Inspection' : 'Work order')} opened on ${component.serial_number}`);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, inspection ? 'The inspection was not opened.' : 'The work order was not opened.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={inspection ? `Log inspection · ${component.serial_number}` : `Open work order · ${component.serial_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
            disabled={openWo.isPending}
            onClick={submit}
          >
            {openWo.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {inspection ? 'Open inspection' : 'Open work order'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fieldiq-text">
          The tool goes to the shop (Maintenance) until the order is completed or cancelled.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <DhField label="Type *">
            <select className={DH_INPUT} value={workType} onChange={(e) => setWorkType(e.target.value)}>
              {!inspection && <option value="">Choose…</option>}
              {types.map((t) => (
                <option key={t} value={t}>
                  {DH_WORK_TYPE_LABEL[t] ?? t}
                </option>
              ))}
            </select>
          </DhField>
          <DhField label="Priority">
            <select className={DH_INPUT} value={priority} onChange={(e) => setPriority(e.target.value)}>
              {DH_WO_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {DH_PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </DhField>
        </div>
        <DhField
          label={inspection ? 'What to check' : 'What to do *'}
          hint={inspection ? `Left blank, it reads "${DH_WORK_TYPE_LABEL[workType] ?? 'Inspection'} of ${component.serial_number}".` : undefined}
        >
          <textarea className={`${DH_INPUT} h-20 py-2`} value={description} maxLength={10000} onChange={(e) => setDescription(e.target.value)} />
        </DhField>
        <div className="grid grid-cols-2 gap-3">
          {canAssign && (
            <DhField label="Assigned to" hint={assignees.data ? undefined : 'Loading people…'}>
              <select className={DH_INPUT} value={assignedTo} disabled={!assignees.data} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">Not assigned</option>
                {assignees.people.map((u) => (
                  <option key={u.id} value={u.id}>
                    {personName(u)}
                  </option>
                ))}
              </select>
            </DhField>
          )}
          <DhField label="Due">
            <input type="date" className={DH_DATE_INPUT} value={due} onChange={(e) => setDue(e.target.value)} />
          </DhField>
        </div>
      </div>
    </DhDialog>
  );
}

interface PartDraft {
  part: string;
  qty: string;
  cost: string;
}
const toDraft = (p: DhPartLine): PartDraft => ({ part: p.part, qty: String(p.qty), cost: String(p.cost) });

/** The parts as the API would store them, or the problems that stop the send. */
export function readParts(drafts: PartDraft[]): { list: DhPartLine[]; problems: string[] } {
  const problems: string[] = [];
  const list: DhPartLine[] = [];
  drafts.forEach((d, i) => {
    const n = i + 1;
    if (!d.part.trim() && !d.cost.trim()) return; // a row added and left blank is not a line
    if (!d.part.trim()) problems.push(`Part ${n}: name it`);
    if (!NUMBER.test(d.qty.trim()) || Number(d.qty) <= 0) problems.push(`Part ${n}: quantity must be more than 0`);
    if (!NUMBER.test(d.cost.trim())) problems.push(`Part ${n}: unit cost must be 0 or more`);
    list.push({ part: d.part.trim(), qty: Number(d.qty), cost: Number(d.cost) });
  });
  return { list, problems };
}

const sameParts = (a: DhPartLine[], b: DhPartLine[]) =>
  a.length === b.length && a.every((p, i) => p.part === b[i].part && p.qty === b[i].qty && p.cost === b[i].cost);

export function EditWorkOrderDialog({
  open,
  onClose,
  wo,
  currency,
}: {
  open: boolean;
  onClose: () => void;
  wo: DhWorkOrder;
  currency: string | null;
}) {
  const update = useUpdateWorkOrder();
  const stored = wo.parts_list ?? [];
  const [priority, setPriority] = useState(wo.priority ?? '');
  const [due, setDue] = useState(wo.due_date ?? '');
  const [description, setDescription] = useState(wo.description ?? '');
  const [findings, setFindings] = useState(wo.findings ?? '');
  const [resolution, setResolution] = useState(wo.resolution ?? '');
  const [labor, setLabor] = useState(wo.labor_hours == null ? '' : String(wo.labor_hours));
  const [parts, setParts] = useState<PartDraft[]>(stored.map(toDraft));

  const setPart = (i: number, patch: Partial<PartDraft>) => setParts((all) => all.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const submit = async () => {
    const problems: string[] = [];
    if (!description.trim()) problems.push('A work order needs a description');
    if (labor.trim() && !HOURS.test(labor.trim())) problems.push('Labour hours must be a number');
    if (due && !DAY.test(due)) problems.push('Due must be a date');
    const read = readParts(parts);
    problems.push(...read.problems);
    if (problems.length) {
      toast.error(problems.slice(0, 3).join('. ') + (problems.length > 3 ? ` (and ${problems.length - 3} more)` : ''));
      return;
    }
    const body: DhWorkOrderWrite = {};
    if (priority && priority !== (wo.priority ?? '')) body.priority = priority;
    if (due !== (wo.due_date ?? '')) body.due_date = due || null;
    if (description.trim() !== (wo.description ?? '')) body.description = description.trim();
    if (findings.trim() !== (wo.findings ?? '')) body.findings = findings.trim() || null;
    if (resolution.trim() !== (wo.resolution ?? '')) body.resolution = resolution.trim() || null;
    // Blank over a stored figure clears it (the API takes null); blank over
    // nothing is no change.
    const laborNext = labor.trim() ? Number(labor.trim()) : null;
    if (laborNext !== (wo.labor_hours ?? null)) body.labor_hours = laborNext;
    // `[]` and `null` are different writes: an emptied list is CLEARED (null),
    // and a list that never existed and is still empty is not sent at all.
    if (!sameParts(read.list, stored)) body.parts_list = read.list.length ? read.list : null;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    try {
      await update.mutateAsync({ id: wo.id, body });
      toast.success(`${wo.wo_number ?? 'Work order'} saved`);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The work order was not saved.'));
    }
  };

  return (
    <DhDialog
      open={open}
      wide
      title={`Edit ${wo.wo_number ?? 'work order'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" disabled={update.isPending} onClick={submit}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <DhField label="Priority">
            <select className={DH_INPUT} value={priority} onChange={(e) => setPriority(e.target.value)}>
              {!priority && <option value="">—</option>}
              {DH_WO_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {DH_PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </DhField>
          <DhField label="Due">
            <input type="date" className={DH_DATE_INPUT} value={due} onChange={(e) => setDue(e.target.value)} />
          </DhField>
          <DhField label="Labour (h)">
            <input className={DH_INPUT} inputMode="decimal" value={labor} onChange={(e) => setLabor(e.target.value)} />
          </DhField>
        </div>
        <DhField label="Description *">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={description} maxLength={10000} onChange={(e) => setDescription(e.target.value)} />
        </DhField>
        <div className="grid gap-3 sm:grid-cols-2">
          <DhField label="Findings">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={findings} maxLength={10000} onChange={(e) => setFindings(e.target.value)} />
          </DhField>
          <DhField label="Resolution">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={resolution} maxLength={10000} onChange={(e) => setResolution(e.target.value)} />
          </DhField>
        </div>

        <fieldset>
          <legend className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            Parts{currency ? ` · unit cost in ${currency}` : ''}
          </legend>
          {parts.length === 0 ? (
            <p className="text-sm text-fieldiq-text">No parts on this order.</p>
          ) : (
            <div className="space-y-2">
              {parts.map((p, i) => (
                <div key={i} className="grid grid-cols-[minmax(0,1fr)_5rem_7rem_2.5rem] gap-2">
                  <input className={DH_INPUT} aria-label={`Part ${i + 1}`} placeholder="Part" value={p.part} maxLength={200} onChange={(e) => setPart(i, { part: e.target.value })} />
                  <input className={DH_INPUT} aria-label={`Quantity of part ${i + 1}`} placeholder="Qty" inputMode="decimal" value={p.qty} onChange={(e) => setPart(i, { qty: e.target.value })} />
                  <input className={DH_INPUT} aria-label={`Unit cost of part ${i + 1}`} placeholder="Unit cost" inputMode="decimal" value={p.cost} onChange={(e) => setPart(i, { cost: e.target.value })} />
                  <button
                    type="button"
                    className={`${DH_SECONDARY} justify-center px-0`}
                    aria-label={`Remove part ${i + 1}`}
                    onClick={() => setParts((all) => all.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className={`${DH_SECONDARY} mt-2`}
            disabled={parts.length >= 200}
            onClick={() => setParts((all) => [...all, { part: '', qty: '1', cost: '' }])}
          >
            <Plus className="size-4" aria-hidden="true" />
            Add part
          </button>
          <p className="mt-2 text-xs text-fieldiq-muted">The parts total is worked out when you save.</p>
        </fieldset>
      </div>
    </DhDialog>
  );
}

export function CancelWorkOrderDialog({ open, onClose, wo }: { open: boolean; onClose: () => void; wo: DhWorkOrder }) {
  const update = useUpdateWorkOrder();
  const [dope, setDope] = useState(false);
  const [needsDope, setNeedsDope] = useState(false);

  const submit = async () => {
    if (!onlineOrSay(toast.error)) return;
    const body: DhWorkOrderWrite = { status: 'cancelled' };
    if (dope) body.dope_clean_confirmed = true;
    try {
      await update.mutateAsync({ id: wo.id, body });
      toast.success(`${wo.wo_number ?? 'Work order'} cancelled`);
      onClose();
    } catch (error) {
      if (conflictCode(error) === 'DOPE_CLEAN_UNCONFIRMED') setNeedsDope(true);
      toast.error(getApiErrorMessage(error, 'The work order was not cancelled.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={`Cancel ${wo.wo_number ?? 'work order'}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Keep it
          </button>
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" disabled={update.isPending} onClick={submit}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Cancel the order
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-fieldiq-text">
        <p>A cancelled order is final. If it is the tool's last open order, the tool goes back to Available.</p>
        <label className={`flex min-h-[2.75rem] items-center gap-3 ${needsDope ? 'font-bold' : ''}`}>
          <input type="checkbox" className="size-5" checked={dope} onChange={(e) => setDope(e.target.checked)} />
          Dope &amp; cleaning done{needsDope ? ' — required before the tool goes back to stock' : ' (needed if this tool goes back to stock)'}
        </label>
      </div>
    </DhDialog>
  );
}
